/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-11 14:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { FormLayout, TextStyle, Link, Icon } from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';

import { Field } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { DropdownIndicator, styles } from './react-select-style';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { ELEMENTS, VIEW_AS_OPTIONS_BY_THEME_V2 } from 'src/constants/theme-settings.js';
import { MENU_ID } from 'src/constants/theme-settings.jsx';
import { URL } from 'src/constants/links';
import { getFullAppUrlV2 } from 'src/helpers';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

class ProductListToolBarForm extends Component<ComposedProps> {
  state = {};

  componentWillReceiveProps(nextProps) {
    const { formValues } = this.props;
    const { formValues: nextFormValues, updateField } = nextProps;

    const isShowViewAs = _.get(formValues, `setting.${ELEMENTS.TOOLBAR_VIEW_AS}.isEnable`, false);
    const isProductCount = _.get(
      formValues,
      `setting.${ELEMENTS.TOOLBAR_PRODUCT_COUNT}.isEnable`,
      false
    );
    const isShowLimit = _.get(formValues, `setting.${ELEMENTS.TOOLBAR_SHOW_LIMIT}.isEnable`, false);
    const isShowSortBy = _.get(formValues, `setting.${ELEMENTS.TOOLBAR_SORT_BY}.isEnable`, false);
    const totalChildren = [isShowViewAs, isProductCount, isShowLimit, isShowSortBy].reduce(
      (cur, next) => {
        return cur + Number(next);
      },
      0
    );

    const nextIsShowViewAs = _.get(
      nextFormValues,
      `setting.${ELEMENTS.TOOLBAR_VIEW_AS}.isEnable`,
      false
    );
    const nextIsProductCount = _.get(
      nextFormValues,
      `setting.${ELEMENTS.TOOLBAR_PRODUCT_COUNT}.isEnable`,
      false
    );
    const nextIsShowLimit = _.get(
      nextFormValues,
      `setting.${ELEMENTS.TOOLBAR_SHOW_LIMIT}.isEnable`,
      false
    );
    const nextIsShowSortBy = _.get(
      nextFormValues,
      `setting.${ELEMENTS.TOOLBAR_SORT_BY}.isEnable`,
      false
    );
    const nextTotalChildren = [
      nextIsShowViewAs,
      nextIsProductCount,
      nextIsShowLimit,
      nextIsShowSortBy
    ].reduce((cur, next) => {
      return cur + Number(next);
    }, 0);

    if (totalChildren !== nextTotalChildren) {
      // Requirement from BA
      switch (nextTotalChildren) {
        case 0:
          updateField(`setting.${MENU_ID.TOOLBAR}.layout`, '');
          break;
        case 1:
          updateField(`setting.${MENU_ID.TOOLBAR}.layout`, '1_1');
          break;
        case 2:
          updateField(`setting.${MENU_ID.TOOLBAR}.layout`, '2_1');
          break;
        case 3:
          updateField(`setting.${MENU_ID.TOOLBAR}.layout`, '3_1');
          break;
        case 4:
          updateField(`setting.${MENU_ID.TOOLBAR}.layout`, '4_2');
          break;
        default:
          break;
      }
    }
  }

  render() {
    const { i18n, boostThemeLibName = 'default' } = this.props;

    const sortByOptionUrl = getFullAppUrlV2('/custom-sorting');

    const listTypeOptions = VIEW_AS_OPTIONS_BY_THEME_V2[boostThemeLibName].map(option => {
      const { label, value } = option;
      return { label: i18n.translate(label), value };
    });

    return (
      <FormLayout>
        <div>
          <span className="form-link">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ThemeSetting.Form.toolBar_UserGuide')}
            </TextStyle>{' '}
            <Link url={URL['ProductListToolbarShopifyIntegration']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        <div className="form-section">
          <div className="form-section__item form-section__item--standard-spacing">
            <Field
              name={`setting.${ELEMENTS.TOOLBAR_VIEW_AS}.isEnable`}
              component={RenderCheckbox}
              label={i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productList_Toolbar_ShowViewAs'
              )}
            />
          </div>

          {listTypeOptions.length > 1 && (
            <div className="form-section__item">
              <Field
                name={`setting.${ELEMENTS.TOOLBAR_VIEW_AS}.listType`}
                component={RenderSelect2}
                options={listTypeOptions}
                label={i18n.translate(
                  'AdminHTML.ShopifyIntegrationSettings.Form.productList_Toolbar_ViewAsDisplay'
                )}
                components={{ DropdownIndicator }}
                styles={styles}
              />
            </div>
          )}

          <div className="form-section__item">
            <Field
              name={`setting.${ELEMENTS.TOOLBAR_PRODUCT_COUNT}.isEnable`}
              component={RenderCheckbox}
              label={i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productList_Toolbar_ShowProductCount'
              )}
            />
          </div>

          <div className="form-section__item">
            <Field
              name={`setting.${ELEMENTS.TOOLBAR_SHOW_LIMIT}.isEnable`}
              component={RenderCheckbox}
              label={i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productList_Toolbar_ShowLimitPerPage'
              )}
            />
          </div>

          <div className="form-section__item">
            <Field
              name={`setting.${ELEMENTS.TOOLBAR_SORT_BY}.isEnable`}
              component={RenderCheckbox}
              label={i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productList_Toolbar_ShowSortBy'
              )}
              anotherProps={{
                helpText: (
                  <p className="description-section">
                    {i18n.translate(
                      'AdminHTML.ThemeSetting.Form.toolBar_ProductCount_SortBy_HelpText',
                      {
                        t1: (
                          <Link url={sortByOptionUrl} external>
                            {i18n.translate(
                              'AdminHTML.ThemeSetting.Form.toolBar_ProductCount_SortBy_HelpText_t1'
                            )}
                          </Link>
                        )
                      }
                    )}
                  </p>
                )
              }}
            />
          </div>
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(ProductListToolBarForm);
